import { useMutation, useQuery } from '@tanstack/react-query';
import { IContact, type IContactFullObject } from '../Interfaces/IContact';
import type {
  ILegalClient,
  ILegalClientFullObject,
} from '../Interfaces/ILegalClient';
import type { ILegalRequirementTemplateFullObject } from '../Interfaces/ILegalRequirementTemplate';
import type {
  ILegalTransaction,
  ILegalTransactionFullObject,
} from '../Interfaces/ILegalTransaction';
import type { IPostcode } from '../Interfaces/IPostcode';
import { customFetcher } from './useFetch';

import type { IAcademicTitle } from '../Interfaces/IAcademicTitle';
import type { IAddressType } from '../Interfaces/IAddressType';
import type { ICourt } from '../Interfaces/IAemter';
import { IArnoTop } from '../Interfaces/IArnoTop';
import { IArnoTopObject } from '../Interfaces/IArnoTopObject';
import type { IBusinessForm } from '../Interfaces/IBusinessForm';
import type { ICompanyFormationOption } from '../Interfaces/ICompanyFormationOption';
import type { ICompanyFormationQuestion } from '../Interfaces/ICompanyFormationQuestion';
import type { IConfig } from '../Interfaces/IConfig';
import type { IContactGroup } from '../Interfaces/IContactGroup';
import type { ICountry } from '../Interfaces/ICountry';
import type { IDayOffWork } from '../Interfaces/IDayOffWork';
import type { IDayOffWorkCategory } from '../Interfaces/IDayOffWorkCategory';
import type { IDialogLegalTransactionTypeMapping } from '../Interfaces/IDialogLegalTransactionTypeMapping';
import type { IDictionary } from '../Interfaces/IDictionary';
import type { IDictionaryEntry } from '../Interfaces/IDictionaryEntry';
import { IDocumentAnalysisAttribute } from '../Interfaces/IDocumentAnalysisAttribute';
import { IDocumentAnalysisSection } from '../Interfaces/IDocumentAnalysisSection';
import { IDocumentAnalysisSubsection } from '../Interfaces/IDocumentAnalysisSubsection';
import { IDocumentAnalysisType } from '../Interfaces/IDocumentAnalysisType';
import { IDocumentDownloaded } from '../Interfaces/IDocumentDownloaded';
import type { IEmploymentContract } from '../Interfaces/IEmploymentContract';
import type {
  IFSDirectory,
  IFsDirectoryFilterSettings,
  IFSScanResult,
} from '../Interfaces/IFSDirectory';
import type { IFSDirectoryAuthentication } from '../Interfaces/IFSDirectoryAuthentication';
import type { IFSDirectoryProtocol } from '../Interfaces/IFSDirectoryProtocol';
import type { IFSDirectoryType } from '../Interfaces/IFSDirectoryType';
import type { IGender } from '../Interfaces/IGender';
import type { IGlobalMail } from '../Interfaces/IGlobalMail';
import { IInventoryOption } from '../Interfaces/IInventoryOption';
import type { ILandMarking } from '../Interfaces/ILandMarking';
import type { ILanguageSection } from '../Interfaces/ILanguageSection';
import type { ILegalClientContactInformationType } from '../Interfaces/ILegalClientContactInformation';
import { ILegalClientRelationship } from '../Interfaces/ILegalClientRelationship/ILegalClientRelationship';
import { ILegalClientRelationshipInput } from '../Interfaces/ILegalClientRelationship/ILegalClientRelationshipInput';
import { ILegalClientType } from '../Interfaces/ILegalClientType';
import type { ILegalRequirement } from '../Interfaces/ILegalRequirement';
import { ILegalRequirementState } from '../Interfaces/ILegalRequirementState';
import { ILegalRoleTemplate } from '../Interfaces/ILegalRoleTemplate';
import { IMeasure } from '../Interfaces/ILegalTransactionMeasure';
import type { ILegalTransactionMeeting } from '../Interfaces/ILegalTransactionMeeting';
import { ILegalTransactionProgress } from '../Interfaces/ILegalTransactionProgress';
import type { ILegalTransactionRecordNumber } from '../Interfaces/ILegalTransactionRecordNumber';
import { ILegalTransactionSpecialFunctions } from '../Interfaces/ILegalTransactionSpecialFunctions';
import { ILegalTransactionState } from '../Interfaces/ILegalTransactionState';
import type { ILegalTransactionTransactionNumber } from '../Interfaces/ILegalTransactionTransactionNumber';
import {
  ILegalTransactionType,
  ILegalTransactionTypeFullObject,
} from '../Interfaces/ILegalTransactionType';
import { IMeetingEmailTemplate } from '../Interfaces/IMeetingEmailTemplate';
import type { IMIMEType } from '../Interfaces/IMIMEType';
import { IPropertyType } from '../Interfaces/IPropertyType';
import { IPurchaseContractRelationType } from '../Interfaces/IPurchaseContractRelationType';
import { IRealEstateUseType } from '../Interfaces/IRealEstateUseType';
import type { IRelationshipRole } from '../Interfaces/IRelationshipRole';
import type { IRelationshipType } from '../Interfaces/IRelationshipType';
import type { IRelationshipTypeHasRelationshipRole } from '../Interfaces/IRelationshipTypeHasRelationshipRole';
import type { IRole } from '../Interfaces/IRole';
import type { ISetting } from '../Interfaces/ISignature';
import { ISystemMail } from '../Interfaces/ISystemMail';
import type { ISystemMail_has_Dictionary } from '../Interfaces/ISystemMail_has_Dictionary';
import type { ISystemMailCategory } from '../Interfaces/ISystemMailCategory';
import type { ITextModule } from '../Interfaces/ITextModule';
import type { ITextModuleCategory } from '../Interfaces/ITextModuleCategory';
import type { ITimeRecording } from '../Interfaces/ITimeRecording';
import type { ITimeRecordingBreak } from '../Interfaces/ITimeRecordingBreak';
import { IUser, IUserFullObject } from '../Interfaces/IUser';
import { IUserGroup } from '../Interfaces/IUserGroup';
import { ISupportRequest } from '../Interfaces/ISupportRequest';
import {
  IChangelog,
  IChangelogViewTrackingEvent,
} from '../Interfaces/IChangelog';
import { IExtension } from '../Interfaces/IExtension';
import { IApp } from '../Interfaces/IApp';
import { IAppSetting } from '../Interfaces/IAppSetting';
import { IDocument } from '../Interfaces/IDocument';
import { IFSDirectorySortedDocument } from '../Interfaces/IFSDirectorySortedDocument';
import { ISendDocumentNew } from '../Interfaces/ISendDocumentNew';

//////////////////// PurchaseContractRelationType
export const useGePurchaseContractRelationType = () => {
  const {
    data: purchaseContractRelationTypes,
    isLoading: isLoadingPurchaseContractRelationTypes,
  } = useQuery<IPurchaseContractRelationType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['purchasecontractrelationtype'],
    queryFn: () =>
      customFetcher({
        endpoint: '/purchasecontractrelationtype',
      }),
  });

  return {
    purchaseContractRelationTypes,
    isLoadingPurchaseContractRelationTypes,
  };
};

//////////////////// RealEstateUseTypes
export const useGeRealEstateUseTypes = () => {
  const { data: realEstateUseTypes, isLoading: isLoadingRealEstateUseTypes } =
    useQuery<IRealEstateUseType[]>({
      refetchOnWindowFocus: false,
      queryKey: ['realestateusetype'],
      queryFn: () =>
        customFetcher({
          endpoint: '/realestateusetype',
        }),
    });

  return { realEstateUseTypes, isLoadingRealEstateUseTypes };
};

//////////////////// InventoryOptions
export const useGeInventoryOptions = () => {
  const { data: inventoryOptions, isLoading: isLoadingInventoryOptions } =
    useQuery<IInventoryOption[]>({
      refetchOnWindowFocus: false,
      queryKey: ['inventoryoption'],
      queryFn: () =>
        customFetcher({
          endpoint: '/inventoryoption',
        }),
    });

  return { inventoryOptions, isLoadingInventoryOptions };
};

//////////////////// PropertyTypes
export const useGePropertyTypes = () => {
  const { data: propertyTypes, isLoading: isLoadingPropertyTypes } = useQuery<
    IPropertyType[]
  >({
    refetchOnWindowFocus: false,
    queryKey: ['propertytype'],
    queryFn: () =>
      customFetcher({
        endpoint: '/propertytype',
      }),
  });

  return { propertyTypes, isLoadingPropertyTypes };
};

//////////////////// LegalRoleTemplates
export const useGetLegalRoleTemplates = () => {
  const {
    data: roleTemplates,
    isLoading: isLoadingRoleTemplates,
    refetch: refetchRoleTemplates,
  } = useQuery<ILegalRoleTemplate[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalroletemplate'],
    queryFn: () =>
      customFetcher({
        endpoint: '/legalroletemplate',
      }),
  });

  return { roleTemplates, isLoadingRoleTemplates, refetchRoleTemplates };
};
//////////////////// Relationships
export const useGetRelationshipsByLegalClientId = (legalClientId: number) => {
  const {
    data: legalClientRelationships,
    isLoading: isLoadingLegalClientRelationships,
    refetch: refetchLegalClientRelationships,
  } = useQuery<ILegalClientRelationship[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalClientRelationships', legalClientId],
    queryFn: () =>
      customFetcher({
        endpoint: `/relationship/getByLegalClient/${legalClientId}`,
      }),
  });

  return {
    legalClientRelationships,
    isLoadingLegalClientRelationships,
    refetchLegalClientRelationships,
  };
};

export const useCreateRelationshipFullObject = () => {
  const {
    mutateAsync: createRelationshipFullObject,
    isPending: isPendingCreateRelationshipFullObject,
  } = useMutation({
    mutationFn: (input: ILegalClientRelationshipInput) =>
      customFetcher({
        endpoint: '/relationship/fullObject',
        method: 'POST',
        body: input,
      }),
  });
  return {
    createRelationshipFullObject,
    isPendingCreateRelationshipFullObject,
  };
};

export const useUpdateRelationshipFullObject = () => {
  const {
    mutateAsync: updateRelationshipFullObject,
    isPending: isPendingUpdateRelationshipFullObject,
  } = useMutation({
    mutationFn: (input: ILegalClientRelationshipInput) =>
      customFetcher({
        endpoint: `/relationship/fullObject`,
        method: 'PUT',
        body: input,
      }),
  });

  return {
    updateRelationshipFullObject,
    isPendingUpdateRelationshipFullObject,
  };
};

export const useDeleteRelationship = () => {
  const {
    mutateAsync: deleteRelationship,
    isPending: isPendingDeleteRelationship,
  } = useMutation({
    mutationFn: (relationshipId: number) =>
      customFetcher({
        endpoint: `/relationship/${relationshipId}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteRelationship,
    isPendingDeleteRelationship,
  };
};

//////////////////// Contacts
export const useGetContacts = () => {
  const {
    data: contacts,
    isLoading: isLoadingContacts,
    refetch: refetchContacts,
  } = useQuery<IContact[]>({
    refetchOnWindowFocus: false,
    queryKey: ['contact'],
    queryFn: () =>
      customFetcher({
        endpoint: '/contact',
      }),
  });

  return { contacts, isLoadingContacts, refetchContacts };
};

export const useGetContactFullObject = (idContact: number) => {
  const {
    data: contactFullObject,
    isLoading: isLoadingContactFullObject,
    refetch: refetchContactFullObject,
  } = useQuery<IContactFullObject>({
    refetchOnWindowFocus: false,
    queryKey: ['contactFullObject', idContact],
    queryFn: () =>
      customFetcher({
        endpoint: `/contact/fullObject/${idContact}`,
      }),
  });

  return {
    contactFullObject,
    isLoadingContactFullObject,
    refetchContactFullObject,
  };
};

export const useUpdateContactFullObject = () => {
  const {
    mutateAsync: updateContactFullObject,
    isPending: isUpdatingContactFullObject,
  } = useMutation({
    mutationFn: (contactFullObject: IContactFullObject) =>
      customFetcher({
        endpoint: `/contact/fullObject/${contactFullObject.idContact}`,
        method: 'PUT',
        body: contactFullObject,
      }),
  });

  return {
    updateContactFullObject,
    isUpdatingContactFullObject,
  };
};

export const useCreateContactFullObject = () => {
  const {
    mutateAsync: createContactFullObject,
    isPending: isCreatingContactFullObject,
  } = useMutation({
    mutationFn: (contactFullObject: IContactFullObject) =>
      customFetcher({
        endpoint: '/contact/fullObject',
        method: 'POST',
        body: contactFullObject,
      }),
  });

  return {
    createContactFullObject,
    isCreatingContactFullObject,
  };
};

export const useDeleteContactFullObject = () => {
  const {
    mutateAsync: deleteContactFullObject,
    isPending: isDeletingContactFullObject,
  } = useMutation({
    mutationFn: (contactId: number) =>
      customFetcher({
        endpoint: `/contact/fullObject/${contactId}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteContactFullObject,
    isDeletingContactFullObject,
  };
};

//////////////////// LegalClients
export const useGetLegalClients = () => {
  const {
    data: legalClients,
    isLoading: isInitialLoadingLegalClients,
    isRefetching: isRefetchingLegalClients,
    refetch: refetchLegalClients,
  } = useQuery<ILegalClient[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalClients'],
    queryFn: () => customFetcher({ endpoint: '/legalclient' }),
  });

  return {
    legalClients,
    isLoadingLegalClients: isInitialLoadingLegalClients,
    isRefetchingLegalClients,
    refetchLegalClients,
  };
};

export const useGetLegalClientFullObject = (idLegalClient: number) => {
  const {
    data: legalClientFullObject,
    isLoading: isLoadingLegalClientFullObject,
    refetch: refetchLegalClientFullObject,
  } = useQuery<ILegalClientFullObject>({
    queryKey: ['legalclient/fullObject', idLegalClient],
    queryFn: () =>
      customFetcher({ endpoint: `/legalclient/fullObject/${idLegalClient}` }),
  });

  return {
    legalClientFullObject,
    isLoadingLegalClientFullObject,
    refetchLegalClientFullObject,
  };
};

export const useCreateLegalClientFullObject = () => {
  const {
    mutateAsync: createLegalClientFullObject,
    isPending: isPendingCreateLegalClientFullObject,
  } = useMutation({
    mutationFn: (legalClient: ILegalClientFullObject) =>
      customFetcher({
        endpoint: '/legalclient/fullObject',
        method: 'POST',
        body: legalClient,
      }),
  });

  return {
    createLegalClientFullObject,
    isPendingCreateLegalClientFullObject,
  };
};

export const useUpdateLegalClientFullObject = () => {
  const {
    mutateAsync: updateLegalClientFullObject,
    isPending: isPendingUpdateLegalClientFullObject,
  } = useMutation({
    mutationFn: (legalClient: ILegalClientFullObject) =>
      customFetcher({
        endpoint: '/legalclient/fullObject',
        method: 'PUT',
        body: legalClient,
      }),
  });
  return {
    updateLegalClientFullObject,
    isPendingUpdateLegalClientFullObject,
  };
};

export const useGetDownloadedDocuments = () => {
  const {
    data: downloadedDocuments,
    isLoading: isLoadingDownloadedDocuments,
    refetch: refetchDownloadedDocuments,
  } = useQuery<IDocumentDownloaded[]>({
    refetchOnWindowFocus: false,
    queryKey: ['documentdownloaded'],
    queryFn: () => customFetcher({ endpoint: '/documentdownloaded' }),
  });

  return {
    downloadedDocuments,
    isLoadingDownloadedDocuments,
    refetchDownloadedDocuments,
  };
};

export const useDeleteLegalClient = () => {
  const {
    mutateAsync: deleteLegalClient,
    isPending: isPendingDeleteLegalClient,
  } = useMutation({
    mutationFn: (id: number) =>
      customFetcher({
        endpoint: `/legalclient/${id}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteLegalClient,
    isPendingDeleteLegalClient,
  };
};

//////////////////// ARNOtop
export const useGetARNOtopNoSync = () => {
  const {
    data: arnoTopNoSyncArray,
    isLoading: isLoadingArnoTopNoSyncArray,
    refetch: refetchArnoTopNoSyncArray,
  } = useQuery<IArnoTop[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalclient/arnotop/no_sync'],
    queryFn: () => customFetcher({ endpoint: `/legalclient/arnotop/no_sync` }),
  });

  return {
    arnoTopNoSyncArray,
    isLoadingArnoTopNoSyncArray,
    refetchArnoTopNoSyncArray,
  };
};

export const useCreateFromArnoTopNoSync = () => {
  const {
    mutateAsync: createFromArnoTopNoSync,
    isPending: isPendingCreateFromArnoTopNoSync,
  } = useMutation({
    mutationFn: (idArnoTop: number) =>
      customFetcher({
        endpoint: `/legalclient/arnotop/import/${idArnoTop}`,
      }),
  });

  return {
    createFromArnoTopNoSync,
    isPendingCreateFromArnoTopNoSync,
  };
};

export const useSearchLegalTransactions = (searchUri?: string) => {
  const abortController = new AbortController();

  const {
    data: legalTransactions,
    isLoading: isLoadingLegalTransactions,
    refetch: refetchLegalTransactions,
  } = useQuery<ILegalTransaction[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransaction', 'search', searchUri],
    queryFn: () =>
      customFetcher({
        endpoint: `/legaltransaction?${searchUri}`,
        signal: abortController.signal,
      }),
    enabled: !!searchUri,
  });

  const abort = () => {
    if (isLoadingLegalTransactions) {
      abortController.abort();
    }
  };

  return {
    legalTransactions,
    isLoadingLegalTransactions,
    refetchLegalTransactions,
    abortSearchLegalTransactions: abort,
  };
};

export const useSearchPostcodes = (searchTerm?: string) => {
  const abortController = new AbortController();

  const searchTermTrimmed = searchTerm?.trim();

  const { data: postcodes, isLoading: isLoadingPostcodes } = useQuery<
    IPostcode[]
  >({
    refetchOnWindowFocus: false,
    queryKey: ['postcode', 'search', searchTermTrimmed],
    queryFn: () =>
      customFetcher({
        endpoint: `/postcode/search/${searchTermTrimmed}`,
        signal: abortController.signal,
      }),
    // Only search if the search term is not empty and has more than 2 characters to avoid unnecessary requests
    enabled: !!searchTermTrimmed && searchTermTrimmed.length >= 2,
  });

  const abort = () => {
    if (isLoadingPostcodes) {
      abortController.abort();
    }
  };

  return { postcodes, isLoadingPostcodes, abortSearchPostcodes: abort };
};

export const useGetLegalTransactions = () => {
  const {
    data: legalTransactions,
    isLoading: isLoadingLegalTransactions,
    refetch: refetchLegalTransactions,
  } = useQuery<ILegalTransaction[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransaction'],
    queryFn: () => customFetcher({ endpoint: '/legaltransaction' }),
  });

  return {
    legalTransactions,
    isLoadingLegalTransactions,
    refetchLegalTransactions,
  };
};

export const useGetLegalTransactionFullObject = (
  idLegalTransaction?: number
) => {
  const {
    data: legalTransactionFullObject,
    isLoading: isLoadingLegalTransactionFullObject,
    refetch: refetchLegalTransactionFullObject,
  } = useQuery<ILegalTransactionFullObject>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransaction/fullObject', idLegalTransaction],
    queryFn: () =>
      customFetcher({
        endpoint: `/legaltransaction/fullObject/${idLegalTransaction}`,
      }),
    enabled: !!idLegalTransaction,
  });

  return {
    legalTransactionFullObject,
    isLoadingLegalTransactionFullObject,
    refetchLegalTransactionFullObject,
  };
};

export const useGetAddressTypes = () => {
  const { data: addressTypes, isLoading: isLoadingAddressTypes } = useQuery<
    IAddressType[]
  >({
    refetchOnWindowFocus: false,
    queryKey: ['addresstype'],
    queryFn: () => customFetcher({ endpoint: '/addresstype' }),
  });

  return { addressTypes, isLoadingAddressTypes };
};

export const useGetLegalClientTypes = () => {
  const {
    data: legalClientTypes,
    isLoading: isLoadingLegalClientTypes,
    refetch: refetchLegalClientTypes,
  } = useQuery<ILegalClientType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalclienttype'],
    queryFn: () => customFetcher({ endpoint: '/legalclienttype' }),
  });

  return {
    legalClientTypes,
    isLoadingLegalClientTypes,
    refetchLegalClientTypes,
  };
};

export const useGetLegalClientContactInformationTypes = () => {
  const {
    data: contactInformationTypes,
    isLoading: isLoadingContactInformationTypes,
    refetch: refetchContactInformationTypes,
  } = useQuery<ILegalClientContactInformationType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['contactinformationtype'],
    queryFn: () => customFetcher({ endpoint: '/contactinformationtype' }),
  });

  return {
    contactInformationTypes,
    isLoadingContactInformationTypes,
    refetchContactInformationTypes,
  };
};

export const useGetSystemMail = (systemMailId: number) => {
  const { data: systemMail, isLoading: isLoadingSystemMail } =
    useQuery<ISystemMail>({
      refetchOnWindowFocus: false,
      queryKey: ['systemmail', systemMailId],
      queryFn: () => customFetcher({ endpoint: `/systemmail/${systemMailId}` }),
    });

  return { systemMail, isLoadingSystemMail };
};

export const useGetUserGroups = () => {
  const {
    data: userGroups,
    isLoading: isLoadingUserGroups,
    refetch: refetchUserGroups,
  } = useQuery<IUserGroup[]>({
    refetchOnWindowFocus: false,
    queryKey: ['usergroup'],
    queryFn: () => customFetcher({ endpoint: '/usergroup' }),
  });

  return { userGroups, isLoadingUserGroups, refetchUserGroups };
};

export const useGetLegalTransactionTypes = () => {
  const {
    data: legalTransactionTypes,
    isLoading: isLoadingLegalTransactionTypes,
    refetch: refetchLegalTransactionTypes,
  } = useQuery<ILegalTransactionType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransactiontype'],
    queryFn: () => customFetcher({ endpoint: '/legaltransactiontype' }),
  });

  return {
    legalTransactionTypes,
    isLoadingLegalTransactionTypes,
    refetchLegalTransactionTypes,
  };
};

export const useGetUsers = () => {
  const {
    data: users,
    isLoading: isLoadingUsers,
    refetch: refetchUsers,
  } = useQuery<IUser[]>({
    refetchOnWindowFocus: false,
    queryKey: ['userlist'],
    queryFn: () => customFetcher({ endpoint: '/userlist' }),
  });

  return { users, isLoadingUsers, refetchUsers };
};

export const useCreateLegalTransactionFullObject = () => {
  const {
    mutateAsync: createLegalTransactionFullObject,
    isPending: isPendingCreateLegalTransactionFullObject,
  } = useMutation({
    mutationFn: (uploadObject: ILegalTransactionFullObject) =>
      customFetcher({
        endpoint: '/legaltransaction/fullObject',
        body: uploadObject,
        method: 'POST',
      }),
  });

  return {
    createLegalTransactionFullObject,
    isPendingCreateLegalTransactionFullObject,
  };
};

export const useGetLegalTransactionSpecialFunctions = () => {
  const {
    data: legalTransactionSpecialFunctions,
    isLoading: isLoadingLegalTransactionSpecialFunctions,
  } = useQuery<ILegalTransactionSpecialFunctions[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransactionspecialfunctions'],
    queryFn: () =>
      customFetcher({ endpoint: '/legaltransactionspecialfunctions' }),
  });

  return {
    legalTransactionSpecialFunctions,
    isLoadingLegalTransactionSpecialFunctions,
  };
};

export const useDeleteLegalTransaction = () => {
  const {
    mutateAsync: deleteLegalTransaction,
    isPending: isPendingDeleteLegalTransaction,
  } = useMutation({
    mutationFn: (idLegalTransaction: number) =>
      customFetcher({
        endpoint: `/legaltransaction/${idLegalTransaction}`,
        method: 'DELETE',
      }),
  });

  return { deleteLegalTransaction, isPendingDeleteLegalTransaction };
};
export const useExportPDF = () => {
  const { mutateAsync: exportPDF, isPending: isPendingExportPDF } = useMutation(
    {
      mutationFn: (uploadObject: ILegalTransactionFullObject) =>
        customFetcher({
          endpoint: '/legaltransaction/export/pdf',
          body: uploadObject,
          method: 'POST',
        }),
    }
  );

  return { exportPDF, isPendingExportPDF };
};

export const useGetHasDirectory = (fileId: number) => {
  const { data: hasDirectory, isLoading: isLoadingHasDirectory } =
    useQuery<boolean>({
      refetchOnWindowFocus: false,
      queryKey: ['fsdirectory/hasDir', fileId],
      queryFn: () =>
        customFetcher({ endpoint: `/fsdirectory/hasDir/${fileId}` }),
    });

  return { hasDirectory, isLoadingHasDirectory };
};
export const useGetLegalRequirementStates = () => {
  const {
    data: legalRequirementStates,
    isLoading: isLoadingLegalRequirementStates,
    refetch: refetchLegalRequirementStates,
  } = useQuery<ILegalRequirementState[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementstate'],
    queryFn: () => customFetcher({ endpoint: '/legalrequirementstate' }),
  });

  return {
    legalRequirementStates,
    isLoadingLegalRequirementStates,
    refetchLegalRequirementStates,
  };
};
export const useGetLegalRequirementTemplates = () => {
  const {
    data: legalRequirementTemplates,
    isLoading: isLoadingLegalRequirementTemplates,
    refetch: refetchLegalRequirementTemplates,
    isRefetching: isRefetchingLegalRequirementTemplates,
  } = useQuery<ILegalRequirementTemplateFullObject[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementtemplate'],
    queryFn: () => customFetcher({ endpoint: '/legalrequirementtemplate' }),
  });
  return {
    legalRequirementTemplates,
    isLoadingLegalRequirementTemplates,
    refetchLegalRequirementTemplates,
    isRefetchingLegalRequirementTemplates,
  };
};
export const useGetLegalRequirementTemplateFullObjects = () => {
  const {
    data: legalRequirementTemplateFullObjects,
    isLoading: isLoadinglegalRequirementTemplateFullObjects,
    refetch: refetchlegalRequirementTemplateFullObjects,
    isRefetching: isRefetchinglegalRequirementTemplateFullObjects,
  } = useQuery<ILegalRequirementTemplateFullObject[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementtemplate/getfullobjects'],
    queryFn: () =>
      customFetcher({ endpoint: '/legalrequirementtemplate/getfullobjects' }),
  });
  return {
    legalRequirementTemplateFullObjects,
    isLoadinglegalRequirementTemplateFullObjects,
    refetchlegalRequirementTemplateFullObjects,
    isRefetchinglegalRequirementTemplateFullObjects,
  };
};

export const useGetLegalRequirementTemplateById = (
  idLegalRequirement: number
) => {
  const {
    data: legalRequirementTemplate,
    isLoading: isLoadingLegalRequirementTemplate,
    isFetching: isLegalRequirementTemplateFetching,
    //isRefetching
    refetch: refetchLegalRequirementTemplate,
  } = useQuery<ILegalRequirementTemplateFullObject>({
    refetchOnWindowFocus: false,
    queryKey: ['/legalrequirementtemplate/fullObject/', idLegalRequirement],
    queryFn: () =>
      customFetcher({
        endpoint: `/legalrequirementtemplate/fullObject/${idLegalRequirement}`,
      }),
  });
  return {
    legalRequirementTemplate,
    isLoadingLegalRequirementTemplate,
    refetchLegalRequirementTemplate,
    isLegalRequirementTemplateFetching,
  };
};

export const useGetLegalRequirementTemplateFullObjectsByIds = (
  idsLegalRequirement: number[]
) => {
  const idsParam = idsLegalRequirement.join(','); // IDs in CSV-Format umwandeln
  const {
    data: LegalRequirementTemplateFullObjectsByIds,
    isLoading: isLoadingLegalRequirementTemplateFullObjectsByIds,
    refetch: refetchLegalRequirementTemplateFullObjectsByIds,
  } = useQuery<ILegalRequirementTemplateFullObject[]>({
    refetchOnWindowFocus: false,
    queryKey: [
      '/legalrequirementtemplate/getlegalrequirementtemplatefullobjectsbyids',
      idsLegalRequirement,
    ],
    queryFn: () =>
      customFetcher({
        endpoint: `/legalrequirementtemplate/getlegalrequirementtemplatefullobjectsbyids?ids=${idsParam}`,
        method: 'GET',
      }),
    enabled: idsLegalRequirement.length > 0,
  });
  return {
    LegalRequirementTemplateFullObjectsByIds,
    isLoadingLegalRequirementTemplateFullObjectsByIds,
    refetchLegalRequirementTemplateFullObjectsByIds,
  };
};

export const useUpdateLegalRequirementTemplates = () => {
  const {
    mutateAsync: updateLegalRequirementTemplates,
    isPending: isPendingUpdateLegalRequirementTemplates,
  } = useMutation({
    mutationFn: (uploadObject: ILegalRequirementTemplateFullObject) =>
      customFetcher({
        endpoint: '/legalrequirementtemplate/updatefullobject',
        body: uploadObject,
        method: 'PUT',
      }),
  });
  return {
    updateLegalRequirementTemplates,
    isPendingUpdateLegalRequirementTemplates,
  };
};

export const useGetLegalTransactionProgresses = () => {
  const {
    data: legalTransactionProgresses,
    isLoading: isLoadingLegalTransactionProgresses,
    refetch: refetchLegalTransactionProgresses,
  } = useQuery<ILegalTransactionProgress[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransactionprogress'],
    queryFn: () => customFetcher({ endpoint: '/legaltransactionprogress' }),
  });

  return {
    legalTransactionProgresses,
    isLoadingLegalTransactionProgresses,
    refetchLegalTransactionProgresses,
  };
};
export const useGetLegalTransactionStates = () => {
  const {
    data: legalTransactionStates,
    isLoading: isLoadingLegalTransactionStates,
    refetch: refetchLegalTransactionStates,
  } = useQuery<ILegalTransactionState[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legaltransactionstate'],
    queryFn: () => customFetcher({ endpoint: '/legaltransactionstate' }),
  });

  return {
    legalTransactionStates,
    isLoadingLegalTransactionStates,
    refetchLegalTransactionStates,
  };
};

export const useGetMeetingEmailTemplates = () => {
  const {
    data: meetingEmailTemplates,
    isLoading: isLoadingMeetingEmailTemplates,
    refetch: refetchMeetingEmailTemplates,
  } = useQuery<IMeetingEmailTemplate[]>({
    refetchOnWindowFocus: false,
    queryKey: ['meetingemailtemplate'],
    queryFn: () => customFetcher({ endpoint: '/meetingemailtemplate' }),
  });

  return {
    meetingEmailTemplates,
    isLoadingMeetingEmailTemplates,
    refetchMeetingEmailTemplates,
  };
};

export const useSendDocuments = () => {
  const { mutateAsync: sendDocuments, isPending: isPendingSendDocuments } =
    useMutation({
      mutationFn: (uploadObject: ISendDocumentNew) =>
        customFetcher({
          endpoint: '/legaltransaction/export/sendDocuments',
          body: uploadObject,
          method: 'POST',
        }),
    });

  return { sendDocuments, isPendingSendDocuments };
};

export const useUpdateLegalTransactionFullObject = () => {
  const {
    mutateAsync: updateLegalTransactionFullObject,
    isPending: isPendingUpdateLegalTransactionFullObject,
  } = useMutation({
    mutationFn: (uploadObject: ILegalTransactionFullObject) =>
      customFetcher({
        endpoint: '/legaltransaction/fullObject',
        body: uploadObject,
        method: 'PUT',
      }),
  });

  return {
    updateLegalTransactionFullObject,
    isPendingUpdateLegalTransactionFullObject,
  };
};

export const useDocumentAnalysisTypes = () => {
  const {
    data: documentAnalysisTypes,
    isLoading: isLoadingDocumentAnalysisTypes,
  } = useQuery<IDocumentAnalysisType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['documentAnalysisType'],
    queryFn: () => customFetcher({ endpoint: '/documentanalysistype' }),
  });

  return { documentAnalysisTypes, isLoadingDocumentAnalysisTypes };
};

export const useDocumentAnalysisSections = () => {
  const {
    data: documentAnalysisSections,
    isLoading: isLoadingDocumentAnalysisSections,
  } = useQuery<IDocumentAnalysisSection[]>({
    refetchOnWindowFocus: false,
    queryKey: ['documentAnalysisSection'],
    queryFn: () => customFetcher({ endpoint: '/documentanalysissection' }),
  });

  return { documentAnalysisSections, isLoadingDocumentAnalysisSections };
};

export const useDocumentAnalysisSubsections = () => {
  const {
    data: documentAnalysisSubsections,
    isLoading: isLoadingDocumentAnalysisSubsections,
  } = useQuery<IDocumentAnalysisSubsection[]>({
    refetchOnWindowFocus: false,
    queryKey: ['documentAnalysisSubsection'],
    queryFn: () => customFetcher({ endpoint: '/documentanalysissubsection' }),
  });

  return { documentAnalysisSubsections, isLoadingDocumentAnalysisSubsections };
};

export const useDocumentAnalysisAttributes = () => {
  const {
    data: documentAnalysisAttributes,
    isLoading: isLoadingDocumentAnalysisAttributes,
  } = useQuery<IDocumentAnalysisAttribute[]>({
    refetchOnWindowFocus: false,
    queryKey: ['documentAnalysisAttribute'],
    queryFn: () => customFetcher({ endpoint: '/documentanalysisattribute' }),
  });

  return { documentAnalysisAttributes, isLoadingDocumentAnalysisAttributes };
};

export const useGetGenders = () => {
  const { data: genders, isLoading: isLoadingGenders } = useQuery<IGender[]>({
    queryKey: ['gender'],
    queryFn: () => customFetcher({ endpoint: '/gender' }),
  });

  return { genders, isLoadingGenders };
};

export const useGetAcademicTitles = () => {
  const { data: academicTitles, isLoading: isLoadingAcademicTitles } = useQuery<
    IAcademicTitle[]
  >({
    queryKey: ['academictitle'],
    queryFn: () => customFetcher({ endpoint: '/academictitle' }),
  });

  return { academicTitles, isLoadingAcademicTitles };
};

export const useGetCountries = () => {
  const { data: countries, isLoading: isLoadingCountries } = useQuery<
    ICountry[]
  >({
    queryKey: ['country'],
    queryFn: () => customFetcher({ endpoint: '/country' }),
  });

  return { countries, isLoadingCountries };
};

export const useGetBusinessForms = () => {
  const { data: businessForms, isLoading: isLoadingBusinessForms } = useQuery<
    IBusinessForm[]
  >({
    queryKey: ['businessform'],
    queryFn: () => customFetcher({ endpoint: '/businessform' }),
  });

  return { businessForms, isLoadingBusinessForms };
};

export const useGetContactGroups = () => {
  const { data: contactGroups, isLoading: isLoadingContactGroups } = useQuery<
    IContactGroup[]
  >({
    queryKey: ['contactgroup'],
    queryFn: () => customFetcher({ endpoint: '/contactgroup' }),
  });

  return { contactGroups, isLoadingContactGroups };
};

export const useGetRelationshipTypes = () => {
  const { data: relationshipTypes, isLoading: isLoadingRelationshipTypes } =
    useQuery<IRelationshipType[]>({
      queryKey: ['relationshiptype'],
      queryFn: () => customFetcher({ endpoint: '/relationshiptype' }),
    });

  return { relationshipTypes, isLoadingRelationshipTypes };
};

export const useGetRelationshipRoles = () => {
  const { data: relationshipRoles, isLoading: isLoadingRelationshipRoles } =
    useQuery<IRelationshipRole[]>({
      queryKey: ['relationshiprole'],
      queryFn: () => customFetcher({ endpoint: '/relationshiprole' }),
    });

  return { relationshipRoles, isLoadingRelationshipRoles };
};

export const useGetMIMETypes = () => {
  const { data: mimeTypes, isLoading: isLoadingMimeTypes } = useQuery<
    IMIMEType[]
  >({
    queryKey: ['mimetype'],
    queryFn: () => customFetcher({ endpoint: '/mimetype' }),
  });

  return { mimeTypes, isLoadingMimeTypes };
};

export const useGetCompanyFormationQuestions = () => {
  const {
    data: companyFormationQuestions,
    isLoading: isLoadingCompanyFormationQuestions,
  } = useQuery<ICompanyFormationQuestion[]>({
    queryKey: ['companyformationquestion'],
    queryFn: () => customFetcher({ endpoint: '/companyformationquestion' }),
  });

  return { companyFormationQuestions, isLoadingCompanyFormationQuestions };
};

export const useGetCompanyFormationOptions = () => {
  const {
    data: companyFormationOptions,
    isLoading: isLoadingCompanyFormationOptions,
  } = useQuery<ICompanyFormationOption[]>({
    queryKey: ['companyformationoption'],
    queryFn: () => customFetcher({ endpoint: '/companyformationoption' }),
  });

  return { companyFormationOptions, isLoadingCompanyFormationOptions };
};

export const useGetTextModuleCategories = () => {
  const {
    data: textModuleCategories,
    isLoading: isLoadingTextModuleCategories,
  } = useQuery<ITextModuleCategory[]>({
    queryKey: ['textmodulecategory'],
    queryFn: () => customFetcher({ endpoint: '/textmodulecategory' }),
  });

  return { textModuleCategories, isLoadingTextModuleCategories };
};

export const useGetTextModules = () => {
  const { data: textModules, isLoading: isLoadingTextModules } = useQuery<
    ITextModule[]
  >({
    queryKey: ['textmodule'],
    queryFn: () => customFetcher({ endpoint: '/textmodule' }),
  });

  return { textModules, isLoadingTextModules };
};

export const useGetDictionaryEntries = () => {
  const { data: dictionaryEntries, isLoading: isLoadingDictionaryEntries } =
    useQuery<IDictionaryEntry[]>({
      queryKey: ['dictionaryentry'],
      queryFn: () => customFetcher({ endpoint: '/dictionaryentry' }),
    });

  return { dictionaryEntries, isLoadingDictionaryEntries };
};

export const useGetDistrictCourts = () => {
  const { data: districtCourts, isLoading: isLoadingDistrictCourts } = useQuery<
    ICourt[]
  >({
    queryKey: ['districtcourt'],
    queryFn: () => customFetcher({ endpoint: '/districtcourt' }),
  });

  return { districtCourts, isLoadingDistrictCourts };
};

export const useGetLandMarkings = () => {
  const { data: landMarkings, isLoading: isLoadingLandMarkings } = useQuery<
    ILandMarking[]
  >({
    queryKey: ['landmarking'],
    queryFn: () => customFetcher({ endpoint: '/landmarking' }),
  });

  return { landMarkings, isLoadingLandMarkings };
};

export const useGetRelationshipTypeHasRelationshipRole = () => {
  const {
    data: relationshipTypeHasRelationshipRole,
    isLoading: isLoadingRelationshipTypeHasRelationshipRole,
  } = useQuery<IRelationshipTypeHasRelationshipRole[]>({
    queryKey: ['relationshiptype_has_relationshiprole'],
    queryFn: () =>
      customFetcher({ endpoint: '/relationshiptype_has_relationshiprole' }),
  });

  return {
    relationshipTypeHasRelationshipRole,
    isLoadingRelationshipTypeHasRelationshipRole,
  };
};

export const useGetLegalTransactionTypeFullObject = (
  idLegalTransactionType: number
) => {
  const {
    data: legalTransactionTypeFullObject,
    isLoading: isLoadingLegalTransactionTypeFullObject,
    isPending: isLegalTransactionTypeFullObjectPending,
    refetch: refetchLegalTransactionTypeFullObject,
  } = useQuery<ILegalTransactionTypeFullObject>({
    queryKey: ['legaltransactiontype/fullObject', idLegalTransactionType],
    queryFn: () =>
      customFetcher({
        endpoint: `/legaltransactiontype/fullObject/${idLegalTransactionType}`,
      }),
    // Makes sure the query is not executed when the id is 0
    enabled: !!idLegalTransactionType,
  });

  return {
    isLegalTransactionTypeFullObjectPending,
    legalTransactionTypeFullObject,
    isLoadingLegalTransactionTypeFullObject,
    refetchLegalTransactionTypeFullObject,
  };
};

export const useGetLegalTransactionTypeObject = (
  idLegalTransactionType: number
) => {
  const {
    data: legalTransactionTypeObject,
    isLoading: isLoadingLegalTransactionTypeObject,
    isPending: isLegalTransactionTypeObjectPending,
    refetch: refetchLegalTransactionTypeObject,
  } = useQuery<ILegalTransactionTypeFullObject>({
    queryKey: ['legaltransactiontype', idLegalTransactionType],
    queryFn: () =>
      customFetcher({
        endpoint: `/legaltransactiontype/${idLegalTransactionType}`,
      }),
    // Makes sure the query is not executed when the id is 0
    enabled: !!idLegalTransactionType,
  });

  return {
    isLegalTransactionTypeObjectPending,
    legalTransactionTypeObject,
    isLoadingLegalTransactionTypeObject,
    refetchLegalTransactionTypeObject,
  };
};

export const useGetConfig = () => {
  const { data: config, isLoading: isLoadingConfig } = useQuery<IConfig>({
    queryKey: ['config'],
    queryFn: () => customFetcher({ endpoint: '/config' }),
  });

  return { config, isLoadingConfig };
};

export const useGetPublicConfig = () => {
  const { data: config, isLoading: isLoadingConfig } = useQuery<IConfig>({
    queryKey: ['config/public'],
    queryFn: () => customFetcher({ endpoint: '/config/public' }),
  });

  return { config, isLoadingConfig };
};

export const useGetSettings = () => {
  const { data: settings, isLoading: isLoadingSettings } = useQuery<ISetting[]>(
    {
      queryKey: ['setting'],
      queryFn: () => customFetcher({ endpoint: '/setting' }),
    }
  );

  return { settings, isLoadingSettings };
};

export const useGetGlobalMails = () => {
  const { data: globalMails, isLoading: isLoadingGlobalMails } = useQuery<
    IGlobalMail[]
  >({
    queryKey: ['globalmail'],
    queryFn: () => customFetcher({ endpoint: '/globalmail' }),
  });

  return { globalMails, isLoadingGlobalMails };
};

export const useGetCorrupted = () => {
  const { data: corrupted, isLoading: isLoadingCorrupted } = useQuery<any[]>({
    queryKey: ['system/sync/corrupted'],
    queryFn: () => customFetcher({ endpoint: '/system/sync/corrupted' }),
  });

  return { corrupted, isLoadingCorrupted };
};

export const useGetStateObject = () => {
  const { data: stateObject, isLoading: isLoadingStateObject } = useQuery<{
    State: string;
  }>({
    queryKey: ['system/sync/state'],
    queryFn: () => customFetcher({ endpoint: '/system/sync/state' }),
  });

  return { stateObject, isLoadingStateObject };
};

export const useGetDialogMappings = () => {
  const { data: dialogMappings, isLoading: isLoadingDialogMappings } = useQuery<
    IDialogLegalTransactionTypeMapping[]
  >({
    queryKey: ['dialoglegaltransactiontypemapping'],
    queryFn: () =>
      customFetcher({
        endpoint: '/dialoglegaltransactiontypemapping',
      }),
  });

  return { dialogMappings, isLoadingDialogMappings };
};

export const useGetLanguageSections = () => {
  const { data: languageSections, isLoading: isLoadingLanguageSections } =
    useQuery<ILanguageSection[]>({
      queryKey: ['languagesection'],
      queryFn: () => customFetcher({ endpoint: '/languagesection' }),
    });

  return { languageSections, isLoadingLanguageSections };
};

export const useGetFsDirectoryTypes = () => {
  const { data: fsDirectoryTypes, isLoading: isLoadingFsDirectoryTypes } =
    useQuery<IFSDirectoryType[]>({
      queryKey: ['fsdirectorytype'],
      queryFn: () => customFetcher({ endpoint: '/fsdirectorytype' }),
    });

  return { fsDirectoryTypes, isLoadingFsDirectoryTypes };
};

export const useGetFsDirectoryProtocols = () => {
  const {
    data: fsDirectoryProtocols,
    isLoading: isLoadingFsDirectoryProtocols,
  } = useQuery<IFSDirectoryProtocol[]>({
    queryKey: ['fsdirectoryprotocol'],
    queryFn: () => customFetcher({ endpoint: '/fsdirectoryprotocol' }),
  });

  return { fsDirectoryProtocols, isLoadingFsDirectoryProtocols };
};

export const useGetFsDirectoryAuthentications = () => {
  const {
    data: fsDirectoryAuthentications,
    isLoading: isLoadingFsDirectoryAuthentications,
  } = useQuery<IFSDirectoryAuthentication[]>({
    queryKey: ['fsdirectoryauthentication'],
    queryFn: () => customFetcher({ endpoint: '/fsdirectoryauthentication' }),
  });

  return { fsDirectoryAuthentications, isLoadingFsDirectoryAuthentications };
};

export const useGetFsDirectories = () => {
  const { data: fsDirectories, isLoading: isLoadingFsDirectories } = useQuery<
    IFSDirectory[]
  >({
    queryKey: ['fsdirectory'],
    queryFn: () => customFetcher({ endpoint: '/fsdirectory' }),
  });

  return { fsDirectories, isLoadingFsDirectories };
};

export const useGetSystemMails = () => {
  const { data: systemMails, isLoading: isLoadingSystemMails } = useQuery<
    ISystemMail[]
  >({
    queryKey: ['systemmail'],
    queryFn: () => customFetcher({ endpoint: '/systemmail' }),
  });

  return { systemMails, isLoadingSystemMails };
};

export const useGetSystemMailCategories = () => {
  const {
    data: systemMailCategories,
    isLoading: isLoadingSystemMailCategories,
  } = useQuery<ISystemMailCategory[]>({
    queryKey: ['systemmailcategory'],
    queryFn: () => customFetcher({ endpoint: '/systemmailcategory' }),
  });

  return { systemMailCategories, isLoadingSystemMailCategories };
};

export const useGetDictionaries = () => {
  const { data: dictionaries, isLoading: isLoadingDictionaries } = useQuery<
    IDictionary[]
  >({
    queryKey: ['dictionary'],
    queryFn: () => customFetcher({ endpoint: '/dictionary' }),
  });

  return { dictionaries, isLoadingDictionaries };
};

export const useGetSystemMailHasDictionary = () => {
  const {
    data: systemMailHasDictionary,
    isLoading: isLoadingSystemMailHasDictionary,
  } = useQuery<ISystemMail_has_Dictionary[]>({
    queryKey: ['systemmail_has_dictionary'],
    queryFn: () => customFetcher({ endpoint: '/systemmail_has_dictionary' }),
  });

  return { systemMailHasDictionary, isLoadingSystemMailHasDictionary };
};

export const useGetTimeRecordBreaks = () => {
  const { data: timeRecordBreaks, isLoading: isLoadingTimeRecordBreaks } =
    useQuery<ITimeRecordingBreak[]>({
      queryKey: ['timerecordingbreak'],
      queryFn: () => customFetcher({ endpoint: '/timerecordingbreak' }),
    });

  return { timeRecordBreaks, isLoadingTimeRecordBreaks };
};

export const useGetLegalTransactionRecordNumbers = () => {
  const {
    data: legalTransactionRecordNumbers,
    isLoading: isLoadingLegalTransactionRecordNumbers,
  } = useQuery<ILegalTransactionRecordNumber[]>({
    queryKey: ['legaltransactionrecordnumber'],
    queryFn: () =>
      customFetcher({ endpoint: '/legaltransaction/recordnumber' }),
  });

  return {
    legalTransactionRecordNumbers,
    isLoadingLegalTransactionRecordNumbers,
  };
};

export const useGetLegalTransactionTransactionNumbers = () => {
  const {
    data: legalTransactionTransactionNumbers,
    isLoading: isLoadingLegalTransactionTransactionNumbers,
  } = useQuery<ILegalTransactionTransactionNumber[]>({
    queryKey: ['legaltransactiontransactionnumber'],
    queryFn: () =>
      customFetcher({ endpoint: '/legaltransaction/transactionnumber' }),
  });

  return {
    legalTransactionTransactionNumbers,
    isLoadingLegalTransactionTransactionNumbers,
  };
};

export const useGetDaysOffWork = () => {
  const { data: daysOffWork, isLoading: isLoadingDaysOffWork } = useQuery<
    IDayOffWork[]
  >({
    queryKey: ['dayoffwork'],
    queryFn: () => customFetcher({ endpoint: '/dayoffwork' }),
  });

  return { daysOffWork, isLoadingDaysOffWork };
};

export const useGetDaysOffWorkCategories = () => {
  const {
    data: daysOffWorkCategories,
    isLoading: isLoadingDaysOffWorkCategories,
  } = useQuery<IDayOffWorkCategory[]>({
    queryKey: ['dayoffworkcategory'],
    queryFn: () => customFetcher({ endpoint: '/dayoffworkcategory' }),
  });

  return { daysOffWorkCategories, isLoadingDaysOffWorkCategories };
};

export const useGetEmploymentContracts = () => {
  const { data: employmentContracts, isLoading: isLoadingEmploymentContracts } =
    useQuery<IEmploymentContract[]>({
      queryKey: ['employmentcontract'],
      queryFn: () => customFetcher({ endpoint: '/employmentcontract' }),
    });

  return { employmentContracts, isLoadingEmploymentContracts };
};

export const useGetRoles = () => {
  const { data: roles, isLoading: isLoadingRoles } = useQuery<IRole[]>({
    queryKey: ['role'],
    queryFn: () => customFetcher({ endpoint: '/role' }),
  });

  return { roles, isLoadingRoles };
};

export const useGetLegalRequirements = () => {
  const { data: legalRequirements, isLoading: isLoadingLegalRequirements } =
    useQuery<ILegalRequirement[]>({
      queryKey: ['legalrequirement'],
      queryFn: () => customFetcher({ endpoint: '/legalrequirement' }),
    });

  return { legalRequirements, isLoadingLegalRequirements };
};

export const useGetLegalTransactionMeetings = () => {
  const {
    data: legalTransactionMeetings,
    isLoading: isLoadingLegalTransactionMeetings,
  } = useQuery<ILegalTransactionMeeting[]>({
    queryKey: ['legaltransactionmeeting'],
    queryFn: () => customFetcher({ endpoint: '/legaltransactionmeeting' }),
  });

  return { legalTransactionMeetings, isLoadingLegalTransactionMeetings };
};

export const useGetMeasurements = () => {
  const { data: measurements, isLoading: isLoadingMeasurements } = useQuery<
    IMeasure[]
  >({
    queryKey: ['measurement'],
    queryFn: () => customFetcher({ endpoint: '/measurement' }),
  });

  return {
    measurements,
    isLoadingMeasurements,
  };
};

export const useGetArnoTopObjects = () => {
  const { data: arnoTopObjects, isLoading: isLoadingArnoTopObjects } = useQuery<
    IArnoTopObject[]
  >({
    refetchOnWindowFocus: false,
    queryKey: ['arnotopsync/arnotop_objects'],
    queryFn: () => customFetcher({ endpoint: '/arnotopsync/arnotop_objects' }),
  });

  return { arnoTopObjects, isLoadingArnoTopObjects };
};

export const useGetAdressType = () => {
  const { data: adressTypeArray, isLoading: isLoadingAddressTypeArray } =
    useQuery<IAddressType[]>({
      refetchOnWindowFocus: false,
      queryKey: ['adresstype'],
      queryFn: () => customFetcher({ endpoint: '/adresstype' }),
    });

  return { adressTypeArray, isLoadingAddressTypeArray };
};

export const useGetArnoTopData = () => {
  const {
    data: arnoTopData,
    isLoading: isLoadingArnoTopData,
    refetch: refetchArnoTopData,
  } = useQuery<IArnoTop[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalclient/arnotop/get_all'],
    queryFn: () => customFetcher({ endpoint: '/legalclient/arnotop/get_all' }),
  });

  return { arnoTopData, isLoadingArnoTopData, refetchArnoTopData };
};

export const useGetLegalClientRelationsByLegalClientId = (
  legalClientId: number
) => {
  const {
    data: legalClientRelations,
    isLoading: isLoadingLegalClientRelations,
    refetch: refetchLegalClientRelations,
  } = useQuery<ILegalClientRelationship[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalclientrelationship'],
    queryFn: () =>
      customFetcher({
        endpoint: `/relationship/getByLegalClient/${legalClientId}`,
      }),
  });

  return {
    legalClientRelations,
    isLoadingLegalClientRelations,
    refetchLegalClientRelations,
  };
};

export const useGetChangelog = () => {
  const {
    data: changelog,
    isLoading: isLoadingChangelog,
    refetch: refetchChangelog,
  } = useQuery<IChangelog>({
    queryKey: ['changelog'],
    //queryFn: () => Promise.resolve(dummyChangelog),
    queryFn: () =>
      customFetcher({
        endpoint: `/changelog`,
      }),
  });

  return { changelog, isLoadingChangelog, refetchChangelog };
};

export const useTrackReleaseView = () => {
  const {
    mutateAsync: trackReleaseView,
    isPending: isPendingTrackReleaseView,
  } = useMutation({
    mutationFn: (changelogViewTrackingEvent: IChangelogViewTrackingEvent) =>
      customFetcher({
        endpoint: '/changelog/track-view',
        method: 'POST',
        body: changelogViewTrackingEvent,
      }),
  });

  return { trackReleaseView, isPendingTrackReleaseView };
};

export const usePostSupportRequest = () => {
  const {
    mutateAsync: postSupportRequest,
    isPending: isPendingPostSupportRequest,
  } = useMutation({
    mutationFn: (supportRequest: ISupportRequest) =>
      customFetcher({
        endpoint: '/support',
        method: 'POST',
        body: supportRequest,
      }),
  });

  return { postSupportRequest, isPendingPostSupportRequest };
};

export const useGetOwnTimeRecords = () => {
  const {
    data: timeRecords,
    isLoading: isLoadingTimeRecords,
    refetch: refetchTimeRecords,
  } = useQuery<ITimeRecording[]>({
    queryKey: ['timerecording'],
    queryFn: () => customFetcher({ endpoint: '/timerecording' }),
  });

  return { timeRecords, isLoadingTimeRecords, refetchTimeRecords };
};

export const useGetAllTimeRecords = () => {
  const {
    data: allTimeRecords,
    isLoading: isLoadingAllTimeRecords,
    refetch: refetchAllTimeRecords,
  } = useQuery<ITimeRecording[]>({
    queryKey: ['timerecording/getAll'],
    queryFn: () => customFetcher({ endpoint: '/timerecording/getAll' }),
  });

  return { allTimeRecords, isLoadingAllTimeRecords, refetchAllTimeRecords };
};

export const useGetUserFullObject = (idUser?: number) => {
  const {
    data: userFullObject,
    isLoading: isLoadingUserFullObject,
    refetch: refetchUserFullObject,
  } = useQuery<IUserFullObject>({
    queryKey: ['user', idUser],
    queryFn: () => customFetcher({ endpoint: `/user/${idUser}` }),
    enabled: !!idUser,
  });

  return { userFullObject, isLoadingUserFullObject, refetchUserFullObject };
};

export const useGetExtensions = () => {
  const {
    data: extensionArray,
    isLoading: isLoadingExtensionArray,
    refetch: refetchExtensionArray,
  } = useQuery<IExtension[]>({
    queryKey: ['app/marketplace'],
    queryFn: () => customFetcher({ endpoint: '/app/marketplace' }),
  });

  return { extensionArray, isLoadingExtensionArray, refetchExtensionArray };
};

export const useScanFsDirectories = (
  filterObject: IFsDirectoryFilterSettings
) => {
  const {
    data: fsScanResults,
    isLoading: isLoadingFsScanResults,
    isFetching: isFetchingFsScanResults,
    refetch: refetchFsScanResults,
  } = useQuery<IFSScanResult[]>({
    queryKey: ['fsdirectory/scan', filterObject],
    queryFn: () =>
      customFetcher({
        endpoint: '/fsdirectory/scan',
        body: filterObject,
        method: 'POST',
      }),
  });

  return {
    fsScanResults,
    isLoadingFsScanResults,
    refetchFsScanResults,
    isFetchingFsScanResults,
  };
};

export const useGetFSSortedDocumentsWithFilter = (
  fsScanResults: IFSScanResult[]
) => {
  const fsDirectoryIds = Array.from(
    new Set(fsScanResults.map(el => el.idFSDirectory))
  ).join(',');

  let endpoint = `/fsdirectorysorteddocument/getWithFilter?directoryIds=${fsDirectoryIds}&filenames=${fsScanResults.map(el => el.fileName).join(',')}`;

  if (!fsScanResults || fsScanResults.length === 0) {
    endpoint = `${endpoint}/-1`;
  }

  const {
    data: fsSortedDocuments,
    isLoading: isLoadingFSSortedDocuments,
    refetch: refetchFSSortedDocuments,
  } = useQuery<IFSDirectorySortedDocument[]>({
    queryKey: ['fsdirectorysorteddocument/getWithFilter', endpoint],
    queryFn: () =>
      customFetcher({
        endpoint,
        method: 'GET',
      }),
  });
  return {
    fsSortedDocuments,
    isLoadingFSSortedDocuments,
    refetchFSSortedDocuments,
  };
};

export const useGetFSDocument = (fsScanResult: IFSScanResult) => {
  const {
    data: fsDocument,
    isLoading: isLoadingFSDocument,
    refetch: refetchFSDocument,
  } = useQuery<IDocument>({
    queryKey: ['fsdirectory/getDoc', fsScanResult],
    queryFn: () =>
      customFetcher({
        endpoint: '/fsdirectory/getDoc',
        body: fsScanResult,
        method: 'POST',
      }),
  });

  return { fsDocument, isLoadingFSDocument, refetchFSDocument };
};

export const useSortFSDirectoryDocument = () => {
  const {
    mutateAsync: sortFSDirectoryDocument,
    isPending: isSortingFSDirectoryDocument,
  } = useMutation({
    mutationFn: (sortedDocument: IFSDirectorySortedDocument) =>
      customFetcher({
        endpoint: '/fsdirectorysorteddocument',
        body: sortedDocument,
        method: 'POST',
      }),
  });

  return { sortFSDirectoryDocument, isSortingFSDirectoryDocument };
};

export const useGetDocument = ({
  idDocument,
  enabled = true,
}: {
  idDocument: number;
  enabled?: boolean;
}) => {
  const {
    data: document,
    isLoading: isLoadingDocument,
    refetch: refetchDocument,
  } = useQuery<IDocument>({
    queryKey: ['document', idDocument],
    queryFn: () => customFetcher({ endpoint: `/document/${idDocument}` }),
    enabled,
  });

  return { document, isLoadingDocument, refetchDocument };
};

export const useGetAppArray = () => {
  const {
    data: appArray,
    isLoading: isLoadingAppArray,
    refetch: refetchAppArray,
  } = useQuery<IApp[]>({
    queryKey: ['app'],
    queryFn: () => customFetcher({ endpoint: '/app' }),
  });

  return { appArray, isLoadingAppArray, refetchAppArray };
};

export const useGetSettingAppArray = () => {
  const {
    data: appSettingArray,
    isLoading: isLoadingAppSettingArray,
    refetch: refetchAppSettingArray,
  } = useQuery<IAppSetting[]>({
    queryKey: ['appsetting'],
    queryFn: () => customFetcher({ endpoint: '/appsetting' }),
  });

  return { appSettingArray, isLoadingAppSettingArray, refetchAppSettingArray };
};

export const useBulkUploadAppSetting = () => {
  const {
    mutateAsync: bulkUplaodAppSetting,
    isPending: isPendingBulkUploadAppSetting,
  } = useMutation({
    mutationFn: (appSetting: IAppSetting[]) =>
      customFetcher({
        endpoint: '/appsetting/bulk_upload',
        method: 'POST',
        body: appSetting,
      }),
  });
  return {
    bulkUplaodAppSetting,
    isPendingBulkUploadAppSetting,
  };
};
